// Reduz uma imagem no próprio navegador ANTES de enviar.
// - Lê o arquivo com createImageBitmap (rápido, sem copiar base64 gigante).
// - Redimensiona para no máximo 800 px no lado maior.
// - Salva em JPEG qualidade 0.7.
// Uma foto de celular de 4–12 MB vira ~100–200 KB, o que faz o envio
// terminar em segundos mesmo em conexão ruim.
const MAX_LADO_PADRAO = 800;
const QUALIDADE_PADRAO = 0.7;

interface FonteImagem {
  largura: number;
  altura: number;
  desenhar: (ctx: CanvasRenderingContext2D, largura: number, altura: number) => void;
  liberar: () => void;
}

// Caminho rápido: o navegador decodifica direto do arquivo.
async function abrirComBitmap(arquivo: File): Promise<FonteImagem | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    // 'from-image' respeita o giro do aparelho (senão a foto sai deitada).
    const criarBitmap = createImageBitmap as unknown as (
      fonte: Blob,
      opcoes?: { imageOrientation?: string },
    ) => Promise<ImageBitmap>;
    const bitmap = await criarBitmap(arquivo, { imageOrientation: 'from-image' });
    return {
      largura: bitmap.width,
      altura: bitmap.height,
      desenhar: (ctx, largura, altura) => ctx.drawImage(bitmap, 0, 0, largura, altura),
      liberar: () => bitmap.close(),
    };
  } catch {
    // HEIC do iPhone ou formato que o bitmap não abre: tenta pelo <img>.
    return null;
  }
}

// Reserva: alguns navegadores antigos não têm createImageBitmap.
function abrirComImg(arquivo: File): Promise<FonteImagem> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      resolve({
        largura: img.naturalWidth,
        altura: img.naturalHeight,
        desenhar: (ctx, largura, altura) => ctx.drawImage(img, 0, 0, largura, altura),
        liberar: () => URL.revokeObjectURL(url),
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível ler a imagem.'));
    };
    img.src = url;
  });
}

async function abrirImagem(arquivo: File): Promise<FonteImagem> {
  const viaBitmap = await abrirComBitmap(arquivo);
  if (viaBitmap) return viaBitmap;
  return abrirComImg(arquivo);
}

export async function reduzirImagem(
  arquivo: File,
  maxLado = MAX_LADO_PADRAO,
  qualidade = QUALIDADE_PADRAO,
  formato: 'image/jpeg' | 'image/webp' = 'image/jpeg',
): Promise<Blob> {
  const fonte = await abrirImagem(arquivo);

  try {
    let { largura, altura } = fonte;
    if (!largura || !altura) throw new Error('Não foi possível processar a imagem.');

    const maior = Math.max(largura, altura);
    if (maior > maxLado) {
      const fator = maxLado / maior;
      largura = Math.max(1, Math.round(largura * fator));
      altura = Math.max(1, Math.round(altura * fator));
    }

    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Não foi possível processar a imagem.');

    // Fundo branco: PNG/HEIC com transparência não vira JPEG preto.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, largura, altura);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    fonte.desenhar(ctx, largura, altura);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, formato, qualidade),
    );
    // Canvas grande demais para o aparelho: cai a qualidade um pouco.
    const final =
      blob ??
      (await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, formato, 0.6)));
    if (!final) throw new Error('Não foi possível processar a imagem.');
    return final;
  } finally {
    fonte.liberar();
  }
}

// Atalho para fotos de pessoas (perfil, ficha, presença): 800 px em JPEG 0.7.
export function reduzirFoto(arquivo: File): Promise<Blob> {
  return reduzirImagem(arquivo, MAX_LADO_PADRAO, QUALIDADE_PADRAO, 'image/jpeg');
}